import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useFetchStore } from './account';
import { useApi } from './api';

// Hook to initiate product sync
export const useInitiateProductSync = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { data: storeData } = useFetchStore();

  return useMutation(
    async (data) => {
      const storeId = storeData?.id;
      const payload = {
        ...data,
        store_id: storeId,
      };
      const response = await api.post('product-sync/sync', payload);
      return {
        success: true,
        data: response.data,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('syncStatus');
        toast.success('Product sync initiated successfully');
      },
      onError: () => {
        toast.error('Something went wrong');
      },
    }
  );
};

// Hook to get sync status for all stores or a specific store
export const useSyncStatus = (storeId = null) => {
  const api = useApi();
  const { data: storeData } = useFetchStore();
  const defaultStoreId = storeData?.id;

  const finalStoreId = storeId || defaultStoreId;
  const url = finalStoreId
    ? `product-sync/status?store_id=${finalStoreId}`
    : 'product-sync/status';

  return useQuery(
    ['syncStatus', finalStoreId],
    async () => {
      const response = await api.get(url);
      return {
        success: true,
        data: response.data,
      };
    },
    {
      refetchOnWindowFocus: false,
    }
  );
};

// Hook to get details of a specific sync request
export const useSyncRequestDetails = (requestId) => {
  const api = useApi();

  return useQuery(
    ['syncRequest', requestId],
    async () => {
      const response = await api.get(`product-sync/requests/${requestId}`);
      // Ensure we always return a consistent format with success property
      return {
        success: true,
        data: response.data,
      };
    },
    {
      // Only fetch if requestId is provided
      enabled: !!requestId,
    }
  );
};

// Hook to poll sync request status
export const usePollSyncRequests = (requestIds = [], interval = 5000) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useQuery(
    ['pollSyncRequests', requestIds],
    async () => {
      if (!requestIds.length) return [];

      const requests = await Promise.all(
        requestIds.map(async (id) => {
          try {
            const response = await api.get(`product-sync/requests/${id}`);
            // Access data directly without assuming nested structure
            return response.data?.data?.data || response.data;
          } catch (error) {
            return null;
          }
        })
      );

      return requests.filter(Boolean);
    },
    {
      enabled: requestIds.length > 0,
      refetchInterval: (data) => {
        // If all requests are completed or failed, stop polling
        if (
          data &&
          data.every(
            (request) =>
              request.status === 'completed' || request.status === 'failed'
          )
        ) {
          // Refresh the overall sync status
          queryClient.invalidateQueries('syncStatus');
          return false;
        }
        return interval;
      },
    }
  );
};
